// =============================================================================
// ResourceDocRedirect.jsx — /resource-doc/:id (the Resources document link target)
// =============================================================================
// Resolves a document link pasted into a Resources page (db-138) to the actual
// file: look the row up by id, sign a short-lived URL, redirect to it.
//
// WHY A ROUTE AND NOT A DIRECT FILE URL: the bucket is private, and a signed URL
// expires — so it can't be stored in page HTML. Indirecting through the row id
// gives a link that is permanent AND access-controlled, and one that survives the
// document being renamed or its file replaced.
//
// The three failure states are deliberately distinguished, because they mean very
// different things to whoever clicked:
//   • removed  — the document existed and was taken down (the link was valid)
//   • missing  — no such document (a bad or mistyped link)
//   • error    — it exists but couldn't be opened (permissions / storage)
// =============================================================================

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isInternalUser } from "../../app/access";
import { getResourceDocumentById } from "../../data/resourceDocuments";
import { getResourceDocSignedUrl } from "../../data/storage";
import { downloadFileName, isInlineViewable } from "../../services/resourceDocs";

const MESSAGES = {
  denied: {
    title: "Not available",
    body: "Resource documents are available to Zephyr staff only.",
  },
  removed: {
    title: "This document is no longer available",
    body: "It has been removed from the Resources document store. The page you came from may need its link updated.",
  },
  missing: {
    title: "Document not found",
    body: "That link doesn’t match any document. It may have been mistyped or copied incompletely.",
  },
  error: {
    title: "Couldn’t open that document",
    body: "The document exists but the file couldn’t be opened. Please try again, or let a supervisor know.",
  },
};

export function ResourceDocRedirect({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = React.useState("loading");

  React.useEffect(
    function () {
      let live = true;
      // Belt and braces: RLS + the storage policy are the real gate, this just
      // avoids a pointless round-trip and gives a clearer message.
      if (!isInternalUser(user)) {
        setState("denied");
        return;
      }
      if (!id) {
        setState("missing");
        return;
      }
      getResourceDocumentById(id).then(async function (row) {
        if (!live) return;
        if (!row) {
          setState("missing");
          return;
        }
        // Deliberately resolved even when deactivated — see getResourceDocumentById.
        if (row.deactivated) {
          setState("removed");
          return;
        }
        // A PDF opens inline; anything else is signed with a download name, or the
        // browser tries to render it and fails ("Couldn't load plugin" on a .docx).
        const inline = isInlineViewable(row.mime_type);
        const url = await getResourceDocSignedUrl(
          row.file_path,
          300,
          inline ? undefined : downloadFileName(row.name, row.file_path)
        );
        if (!live) return;
        if (!url) {
          setState("error");
          return;
        }
        if (inline) {
          setState("opening");
          // replace(), not assign(): the redirect shouldn't sit in the back history,
          // or Back from the file would bounce straight through here again.
          window.location.replace(url);
          return;
        }
        // A download doesn't navigate, so the user would otherwise be left staring
        // at "Opening document…" forever. Trigger it, then say what happened.
        setState("downloaded");
        window.location.href = url;
      });
      return function () {
        live = false;
      };
    },
    [id, user]
  );

  if (state === "loading" || state === "opening") {
    return (
      <div className="p-10 text-center text-zephyr-muted text-[14px]">
        {state === "opening" ? "Opening document…" : "Loading…"}
      </div>
    );
  }

  // A download leaves the page where it is, so say so rather than hanging on
  // "Opening document…" with nothing apparently happening.
  if (state === "downloaded") {
    return (
      <div className="p-6 max-w-[var(--page-max)] mx-auto">
        <div className="border border-zephyr-border rounded-[10px] p-10 bg-white text-center">
          <div className="text-[17px] font-bold text-zephyr-text mb-2">
            Your download has started
          </div>
          <div className="text-[14px] text-zephyr-muted mb-5">
            Check your browser’s downloads if you don’t see it.
          </div>
          <button
            type="button"
            onClick={function () {
              navigate("/general");
            }}
            className="bg-zephyr-teal text-white rounded-[8px] py-[8px] px-5 text-[14px] font-semibold cursor-pointer border-none"
            style={{ fontFamily: "inherit" }}
          >
            Go to Resources
          </button>
        </div>
      </div>
    );
  }

  const msg = MESSAGES[state] || MESSAGES.error;
  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="border border-zephyr-border rounded-[10px] p-10 bg-white text-center">
        <div className="text-[17px] font-bold text-zephyr-text mb-2">{msg.title}</div>
        <div className="text-[14px] text-zephyr-muted mb-5">{msg.body}</div>
        <button
          type="button"
          onClick={function () {
            navigate("/general");
          }}
          className="bg-zephyr-teal text-white rounded-[8px] py-[8px] px-5 text-[14px] font-semibold cursor-pointer border-none"
          style={{ fontFamily: "inherit" }}
        >
          Go to Resources
        </button>
      </div>
    </div>
  );
}
